import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useToast } from '../context/ToastContext';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { AuthContext } from '../context/AuthContext';
import {
    getMostViewed, getRootContents, getFolderContents,
    createFolder, renameFolder, deleteFolder,
    getArticle, createArticle, updateArticle, deleteArticle,
    search as searchApi,
} from '../services/knowledgeBaseService';
import {
    FaBook, FaFolder, FaFolderOpen, FaFileAlt, FaSearch,
    FaPlus, FaEdit, FaTrash, FaEye, FaChevronRight,
    FaHome, FaCheck, FaTag,
} from 'react-icons/fa';
import { can, FEATURE_KEYS } from '../utils/featureAccess';
import './KnowledgeBase.css';

/* ---- helpers ---- */
const formatDate = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return iso; }
};

const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

const autoSummary = (html) => {
    const text = stripHtml(html);
    return text.length > 200 ? text.slice(0, 197) + '…' : text;
};

const getErrorMessage = (err, fallback = 'Something went wrong. Please try again.') => {
    const fromApi = err?.response?.data;
    if (typeof fromApi === 'string' && fromApi.trim()) return fromApi;
    if (fromApi?.message) return fromApi.message;
    if (err?.message) return err.message;
    return fallback;
};

const QUILL_MODULES = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'code-block'],
        ['link'],
        ['clean'],
    ],
};

function RichTextEditor({ value, onChange, placeholder }) {
    const editorElRef = useRef(null);
    const quillRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const initialValueRef = useRef(value);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        if (!editorElRef.current || quillRef.current) return undefined;

        const quill = new Quill(editorElRef.current, {
            theme: 'snow',
            modules: QUILL_MODULES,
            placeholder,
        });
        quillRef.current = quill;

        if (initialValueRef.current) {
            quill.clipboard.dangerouslyPasteHTML(initialValueRef.current);
        }

        const handleTextChange = () => {
            const html = quill.root.innerHTML;
            onChangeRef.current?.(html === '<p><br></p>' ? '' : html);
        };

        quill.on('text-change', handleTextChange);

        return () => {
            quill.off('text-change', handleTextChange);
            quillRef.current = null;
        };
    }, [placeholder]);

    useEffect(() => {
        const quill = quillRef.current;
        if (!quill) return;

        const nextValue = value || '';
        const currentValue = quill.root.innerHTML === '<p><br></p>' ? '' : quill.root.innerHTML;
        if (currentValue === nextValue) return;

        const selection = quill.getSelection();
        if (!nextValue) {
            quill.setText('');
        } else {
            quill.clipboard.dangerouslyPasteHTML(nextValue);
        }

        if (selection) {
            quill.setSelection(selection);
        }
    }, [value]);

    return <div className="kb-quill-editor" ref={editorElRef} />;
}

/* =============================================
   CONFIRM MODAL
   ============================================= */
function ConfirmModal({ message, onConfirm, onCancel }) {
    return (
        <div className="kb-modal-overlay">
            <div className="kb-modal">
                <div className="kb-modal-header">
                    <h3>Confirm</h3>
                </div>
                <div className="kb-modal-body">
                    <p>{message}</p>
                </div>
                <div className="kb-modal-actions">
                    <button className="kb-btn kb-btn-secondary" onClick={onCancel}>Cancel</button>
                    <button className="kb-btn kb-btn-danger" onClick={onConfirm}>Delete</button>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   FOLDER MODAL (create / rename)
   ============================================= */
function FolderModal({ title, initial, onSave, onCancel }) {
    const [name, setName] = useState(initial || '');
    return (
        <div className="kb-modal-overlay">
            <div className="kb-modal">
                <div className="kb-modal-header">
                    <h3>{title}</h3>
                </div>
                <div className="kb-modal-body">
                    <div className="kb-field">
                        <label>Folder Name</label>
                        <input
                            className="kb-input"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && name.trim() && onSave(name.trim())}
                            autoFocus
                        />
                    </div>
                </div>
                <div className="kb-modal-actions">
                    <button className="kb-btn kb-btn-secondary" onClick={onCancel}>Cancel</button>
                    <button className="kb-btn kb-btn-primary" onClick={() => name.trim() && onSave(name.trim())}>
                        <FaCheck /> Save
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   ARTICLE EDITOR VIEW
   ============================================= */
function ArticleEditor({ articleId, initialFolderId, onSaved, onCancel }) {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [tags, setTags] = useState('');
    const [folderId, setFolderId] = useState(initialFolderId || 'root');
    const [allFolders, setAllFolders] = useState([]);
    const [saving, setSaving] = useState(false);
    const { addToast } = useToast();
    const isEdit = !!articleId;

    useEffect(() => {
        // Load all folders for folder picker
        const loadFolders = async () => {
            try {
                const root = await getRootContents();
                const collect = async (folders) => {
                    let result = [...folders];
                    for (const f of folders) {
                        const sub = await getFolderContents(f.id);
                        result = result.concat(await collect(sub.folders || []));
                    }
                    return result;
                };
                const flat = await collect(root.folders || []);
                setAllFolders(flat);
            } catch { }
        };
        loadFolders();

        if (isEdit) {
            getArticle(articleId).then(a => {
                setTitle(a.title || '');
                setContent(a.content || '');
                setTags((a.tags || []).join(', '));
                setFolderId(a.folderId || 'root');
            }).catch(() => { });
        }
    }, [articleId, isEdit]);

    const handleSave = async () => {
        if (!title.trim()) return;
        setSaving(true);
        try {
            const payload = {
                title: title.trim(),
                content,
                summary: autoSummary(content),
                folderId: folderId || 'root',
                tags: tags.split(',').map(t => t.trim()).filter(Boolean),
            };
            if (isEdit) {
                await updateArticle(articleId, payload);
            } else {
                await createArticle(payload);
            }
            onSaved();
        } catch (e) {
            addToast(getErrorMessage(e, 'Failed to save article.'), 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="kb-editor-wrap">
            <div className="kb-panel-header">
                <h2>{isEdit ? 'Edit Article' : 'New Article'}</h2>
            </div>

            <div className="kb-panel-body">
            <div className="kb-field">
                <label>Title *</label>
                <input
                    className="kb-input"
                    placeholder="Enter a clear, descriptive title"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                />
            </div>

            <div className="kb-field">
                <label>Folder</label>
                <select
                    className="kb-select"
                    value={folderId}
                    onChange={e => setFolderId(e.target.value)}
                >
                    <option value="root">— Root (no folder) —</option>
                    {allFolders.map(f => (
                        <option key={f.id} value={f.id}>{f.path || f.name}</option>
                    ))}
                </select>
            </div>

            <div className="kb-field">
                <label>Tags <span className="kb-tag-hint">(comma-separated)</span></label>
                <input
                    className="kb-input"
                    placeholder="e.g. onboarding, HR, policy"
                    value={tags}
                    onChange={e => setTags(e.target.value)}
                />
            </div>

            <div className="kb-field">
                <label>Content</label>
                <div className="kb-quill-wrap">
                    <RichTextEditor
                        value={content}
                        onChange={setContent}
                        placeholder="Write your article here…"
                    />
                </div>
            </div>
            </div>

            <div className="kb-editor-actions">
                <button className="kb-btn kb-btn-secondary" onClick={onCancel}>Cancel</button>
                <button className="kb-btn kb-btn-primary" onClick={handleSave} disabled={saving || !title.trim()}>
                    {saving ? 'Saving…' : <><FaCheck /> {isEdit ? 'Save Changes' : 'Publish Article'}</>}
                </button>
            </div>
        </div>
    );
}

/* =============================================
   ARTICLE VIEWER
   ============================================= */
function ArticleViewer({ articleId, canWrite, canDelete, onEdit, onDeleted, onBack }) {
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const { addToast } = useToast();

    useEffect(() => {
        setLoading(true);
        getArticle(articleId)
            .then(setArticle)
            .catch(() => setArticle(null))
            .finally(() => setLoading(false));
    }, [articleId]);

    const handleDelete = async () => {
        try {
            await deleteArticle(articleId);
            addToast("Article deleted successfully!", "success");
            onDeleted();
        } catch (e) {
            addToast(getErrorMessage(e, 'Failed to delete article.'), 'error');
            setConfirmDelete(false);
        }
    };

    if (loading) return <div className="kb-loading"><div className="kb-spinner" /><span>Loading article…</span></div>;
    if (!article) return <div className="kb-empty"><div className="kb-empty-icon">📄</div><p>Article not found.</p></div>;

    return (
        <>
            {confirmDelete && (
                <ConfirmModal
                    message={`Delete "${article.title}"? This cannot be undone.`}
                    onConfirm={handleDelete}
                    onCancel={() => setConfirmDelete(false)}
                />
            )}
            <div className="kb-article-view">
                <div className="kb-article-view-header">
                    <h1 className="kb-article-view-title">{article.title}</h1>
                    <div className="kb-article-view-meta">
                        <span><FaEye className="kb-meta-icon" />{article.viewCount} views</span>
                        <span>By {article.createdBy}</span>
                        <span>{formatDate(article.updatedAt)}</span>
                        {(article.tags || []).length > 0 && (
                            <div className="kb-tags">
                                {article.tags.map(t => <span key={t} className="kb-tag"><FaTag className="kb-meta-icon" />{t}</span>)}
                            </div>
                        )}
                        {(canWrite || canDelete) && (
                            <div className="kb-article-view-actions">
                                {canWrite && <button className="kb-btn kb-btn-secondary" onClick={onEdit}><FaEdit /> Edit</button>}
                                {canDelete && <button className="kb-btn kb-btn-danger" onClick={() => setConfirmDelete(true)}><FaTrash /> Delete</button>}
                            </div>
                        )}
                    </div>
                </div>
                <div className="kb-panel-body">
                    <div className="kb-article-view-body" dangerouslySetInnerHTML={{ __html: article.content }} />
                    <div className="kb-divider" />
                    <button className="kb-btn kb-btn-secondary" onClick={onBack}>← Back</button>
                </div>
            </div>
        </>
    );
}

/* =============================================
   FOLDER BROWSER VIEW
   ============================================= */
function FolderBrowser({ folderId, canWrite, canDelete, onNavigateFolder, onViewArticle, onNewArticle }) {
    const [data, setData] = useState({ folders: [], articles: [], currentFolder: null });
    const [loading, setLoading] = useState(true);
    const [folderModal, setFolderModal] = useState(null); // null | 'create' | { id, name }
    const [confirmDelete, setConfirmDelete] = useState(null); // null | { type, id, name }
    const { addToast } = useToast();

    const load = useCallback(() => {
        setLoading(true);
        const fetcher = folderId === 'root' ? getRootContents() : getFolderContents(folderId);
        fetcher
            .then(setData)
            .catch((e) => addToast(getErrorMessage(e, 'Failed to load this folder.'), 'error'))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folderId]);

    useEffect(() => { load(); }, [load]);

    const handleCreateFolder = async (name) => {
        try {
            const parentId = folderId === 'root' ? 'root' : folderId;
            await createFolder({ name, parentId });
            addToast("Folder created successfully!", "success");
            setFolderModal(null);
            load();
        } catch (e) {
            addToast(getErrorMessage(e, 'Failed to create folder.'), 'error');
        }
    };

    const handleRenameFolder = async (name) => {
        try {
            await renameFolder(folderModal.id, name);
            addToast("Folder renamed successfully!", "success");
            setFolderModal(null);
            load();
        } catch (e) {
            addToast(getErrorMessage(e, 'Failed to rename folder.'), 'error');
        }
    };

    const handleConfirmDelete = async () => {
        try {
            if (confirmDelete.type === 'folder') {
                await deleteFolder(confirmDelete.id);
                addToast("Folder deleted successfully!", "success");
            } else {
                await deleteArticle(confirmDelete.id);
                addToast("Article deleted successfully!", "success");
            }
            setConfirmDelete(null);
            load();
        } catch (e) {
            addToast(getErrorMessage(e, 'Failed to delete item.'), 'error');
            setConfirmDelete(null);
        }
    };

    if (loading) return <div className="kb-loading"><div className="kb-spinner" /><span>Loading…</span></div>;

    const hasContent = data.folders.length > 0 || data.articles.length > 0;

    return (
        <>
            {folderModal === 'create' && (
                <FolderModal title="New Folder" onSave={handleCreateFolder} onCancel={() => setFolderModal(null)} />
            )}
            {folderModal && typeof folderModal === 'object' && (
                <FolderModal title="Rename Folder" initial={folderModal.name} onSave={handleRenameFolder} onCancel={() => setFolderModal(null)} />
            )}
            {confirmDelete && (
                <ConfirmModal
                    message={`Delete ${confirmDelete.type} "${confirmDelete.name}"? ${confirmDelete.type === 'folder' ? 'All sub-folders and articles inside will also be deleted.' : 'This cannot be undone.'}`}
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setConfirmDelete(null)}
                />
            )}

            {/* Actions */}
            <div className="kb-toolbar">
                <div className="kb-section-title">
                    <FaFolderOpen />
                    {data.currentFolder ? data.currentFolder.name : 'Knowledge Base'}
                </div>
                {canWrite && (
                    <div className="kb-actions">
                        <button className="kb-btn kb-btn-secondary" onClick={() => setFolderModal('create')}>
                            <FaPlus /> New Folder
                        </button>
                        <button className="kb-btn kb-btn-primary" onClick={onNewArticle}>
                            <FaPlus /> New Article
                        </button>
                    </div>
                )}
            </div>

            {!hasContent && (
                <div className="kb-empty">
                    <div className="kb-empty-icon">📂</div>
                    <p>This folder is empty. Create a sub-folder or add an article.</p>
                </div>
            )}

            {hasContent && (
                <div className="kb-wiki-layout">
                    <aside className="kb-wiki-sidebar">
                        <div className="kb-small-heading">Sections</div>
                        {data.folders.length === 0 ? (
                            <div className="kb-wiki-empty">No sub-sections yet.</div>
                        ) : (
                            <div className="kb-wiki-list">
                                {data.folders.map(f => (
                                    <div key={f.id} className="kb-wiki-folder-row" onClick={() => onNavigateFolder(f.id)}>
                                        <div className="kb-wiki-row-main">
                                            <FaFolder className="kb-wiki-folder-icon" />
                                            <span className="kb-wiki-row-title">{f.name}</span>
                                        </div>
                                        <div className="kb-wiki-row-path">{f.path}</div>
                                        {(canWrite || canDelete) && (
                                            <div className="kb-wiki-row-actions" onClick={e => e.stopPropagation()}>
                                                {canWrite && (
                                                    <button className="kb-btn-icon" title="Rename" onClick={() => setFolderModal({ id: f.id, name: f.name })}>
                                                        <FaEdit />
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button className="kb-btn-icon danger" title="Delete" onClick={() => setConfirmDelete({ type: 'folder', id: f.id, name: f.name })}>
                                                        <FaTrash />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </aside>

                    <section className="kb-wiki-main">
                        <div className="kb-small-heading">Pages</div>
                        {data.articles.length === 0 ? (
                            <div className="kb-wiki-empty">No pages in this section yet.</div>
                        ) : (
                            <div className="kb-wiki-list">
                                {data.articles.map(a => (
                                    <div key={a.id} className="kb-wiki-article-row" onClick={() => onViewArticle(a.id)}>
                                        <div className="kb-wiki-row-main">
                                            <FaFileAlt className="kb-article-icon" />
                                            <span className="kb-wiki-row-title">{a.title}</span>
                                        </div>
                                        {a.summary && <div className="kb-wiki-row-summary">{a.summary}</div>}
                                        <div className="kb-article-meta">
                                            <span className="kb-view-count"><FaEye />{a.viewCount}</span>
                                            <span>{a.createdBy}</span>
                                            {(a.tags || []).length > 0 && (
                                                <div className="kb-tags">{a.tags.slice(0, 3).map(t => <span key={t} className="kb-tag">{t}</span>)}</div>
                                            )}
                                        </div>
                                        {canDelete && (
                                            <div className="kb-wiki-row-actions" onClick={e => e.stopPropagation()}>
                                                <button className="kb-btn-icon danger" title="Delete" onClick={() => setConfirmDelete({ type: 'article', id: a.id, name: a.title })}>
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>
            )}
        </>
    );
}

/* =============================================
   SEARCH OVERLAY
   ============================================= */
function SearchResults({ query, onNavigateFolder, onViewArticle }) {
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        if (!query || query.trim().length < 2) { setResults(null); return; }
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            setLoading(true);
            searchApi(query.trim())
                .then(setResults)
                .catch(() => setResults(null))
                .finally(() => setLoading(false));
        }, 350);
        return () => clearTimeout(timerRef.current);
    }, [query]);

    if (!query || query.trim().length < 2) return null;
    if (loading) return <div className="kb-search-results"><div className="kb-spinner kb-spinner-centered" /></div>;
    if (!results) return null;

    const total = (results.folders?.length || 0) + (results.articles?.length || 0);
    if (total === 0) return <div className="kb-search-results"><p className="kb-search-empty-text">No results for "{query}".</p></div>;

    return (
        <div className="kb-search-results">
            {(results.folders || []).length > 0 && (
                <>
                    <h3>Folders</h3>
                    {results.folders.map(f => (
                        <div key={f.id} className="kb-search-item" onClick={() => onNavigateFolder(f.id)}>
                            <FaFolder className="kb-search-item-icon kb-search-folder-icon" />
                            <span className="kb-search-item-text">{f.name}</span>
                            <span className="kb-search-item-type">folder</span>
                        </div>
                    ))}
                </>
            )}
            {(results.articles || []).length > 0 && (
                <>
                    <h3 className={results.folders?.length ? 'kb-search-heading-with-gap' : ''}>Articles</h3>
                    {results.articles.map(a => (
                        <div key={a.id} className="kb-search-item" onClick={() => onViewArticle(a.id)}>
                            <FaFileAlt className="kb-search-item-icon" />
                            <span className="kb-search-item-text">{a.title}</span>
                            <span className="kb-search-item-type">article</span>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
}

/* =============================================
   HOME VIEW — Most Viewed
   ============================================= */
function HomeView({ canWrite, onNavigateFolder, onViewArticle, onNewArticle }) {
    const [mostViewed, setMostViewed] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getMostViewed()
            .then(setMostViewed)
            .catch(() => { })
            .finally(() => setLoading(false));
    }, []);

    return (
        <>
            <div className="kb-home-hero">
                <h1>📚 Heel Knowledge Base</h1>
                <p>Find answers, explore topics, and share knowledge across the team.</p>
                <div className="kb-home-hero-sub">
                    <button className="kb-btn kb-btn-primary" onClick={() => onNavigateFolder('root')}>
                        <FaFolderOpen /> Browse All
                    </button>
                    {canWrite && (
                        <button className="kb-btn kb-btn-ghost-light" onClick={onNewArticle}>
                            <FaPlus /> Write an Article
                        </button>
                    )}
                </div>
            </div>

            <div className="kb-toolbar">
                <div className="kb-section-title"><FaEye /> Most Viewed</div>
            </div>

            {loading && <div className="kb-loading"><div className="kb-spinner" /></div>}
            {!loading && mostViewed.length === 0 && (
                <div className="kb-empty">
                    <div className="kb-empty-icon">📄</div>
                    <p>No articles yet. Be the first to create one!</p>
                </div>
            )}
            {!loading && mostViewed.length > 0 && (
                <div className="kb-wiki-main">
                    <div className="kb-wiki-list">
                        {mostViewed.map(a => (
                            <div key={a.id} className="kb-wiki-article-row" onClick={() => onViewArticle(a.id)}>
                                <div className="kb-wiki-row-main">
                                    <FaFileAlt className="kb-article-icon" />
                                    <span className="kb-wiki-row-title">{a.title}</span>
                                </div>
                                {a.summary && <div className="kb-wiki-row-summary">{a.summary}</div>}
                                <div className="kb-article-meta">
                                    <span className="kb-view-count"><FaEye />{a.viewCount}</span>
                                    <span>{a.createdBy}</span>
                                    {(a.tags || []).length > 0 && (
                                        <div className="kb-tags">{a.tags.slice(0, 3).map(t => <span key={t} className="kb-tag">{t}</span>)}</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}

/* =============================================
   BREADCRUMB
   ============================================= */
function Breadcrumb({ ancestors, currentName, onHome, onNavigate }) {
    return (
        <div className="kb-breadcrumb">
            <button className="kb-breadcrumb-btn" onClick={onHome}><FaHome /> <span style={{ marginLeft: '4px' }}>Home</span></button>
            {(ancestors || []).map((a) => (
                <React.Fragment key={a.id}>
                    <FaChevronRight className="kb-breadcrumb-sep kb-breadcrumb-sep-icon" />
                    <button className="kb-breadcrumb-btn" onClick={() => onNavigate(a.id)}>{a.name}</button>
                </React.Fragment>
            ))}
            {currentName && (
                <>
                    <FaChevronRight className="kb-breadcrumb-sep kb-breadcrumb-sep-icon" />
                    <span className="kb-breadcrumb-current">{currentName}</span>
                </>
            )}
        </div>
    );
}

/* =============================================
   MAIN PAGE
   ============================================= */
export default function KnowledgeBase() {
    const { user } = useContext(AuthContext);
    // Knowledge Base management is gated on the feature's capabilities:
    // write = create/rename folders + create/edit articles; delete = remove them.
    const canWrite = can(user, FEATURE_KEYS.KNOWLEDGE_BASE, "write");
    const canDelete = can(user, FEATURE_KEYS.KNOWLEDGE_BASE, "delete");

    // view: 'home' | 'folder' | 'article' | 'editor'
    const [view, setView] = useState('home');
    const [currentFolderId, setCurrentFolderId] = useState('root');
    const [currentFolderAncestors, setCurrentFolderAncestors] = useState([]);
    const [currentFolderName, setCurrentFolderName] = useState('');
    const [currentArticleId, setCurrentArticleId] = useState(null);
    const [editingArticleId, setEditingArticleId] = useState(null); // null = new
    const [searchQuery, setSearchQuery] = useState('');

    const goHome = () => {
        setView('home');
        setSearchQuery('');
    };

    const navigateFolder = useCallback(async (folderId) => {
        setSearchQuery('');
        if (folderId === 'root') {
            setCurrentFolderId('root');
            setCurrentFolderAncestors([]);
            setCurrentFolderName('');
            setView('folder');
            return;
        }
        try {
            const data = await getFolderContents(folderId);
            const f = data.currentFolder;
            setCurrentFolderId(folderId);
            setCurrentFolderAncestors(f?.ancestors || []);
            setCurrentFolderName(f?.name || '');
        } catch { }
        setView('folder');
    }, []);

    const viewArticle = (articleId) => {
        setCurrentArticleId(articleId);
        setView('article');
        setSearchQuery('');
    };

    const openNewArticle = () => {
        setEditingArticleId(null);
        setView('editor');
    };

    const openEditArticle = (articleId) => {
        setEditingArticleId(articleId);
        setView('editor');
    };

    const onArticleSaved = () => {
        // Go back to where we came from
        if (currentFolderId === 'root') {
            setView('home');
        } else {
            setView('folder');
        }
    };

    const onArticleDeleted = () => {
        setView('folder');
    };

    return (
        <div className="kb-page">
            {/* Top bar */}
            <div className="kb-topbar">
                {/* Navigation tools */}
                <div className="kb-topbar-nav">
                    {view === 'folder' && (
                        <Breadcrumb
                            ancestors={currentFolderAncestors}
                            currentName={currentFolderName || (currentFolderId === 'root' ? 'All' : '')}
                            onHome={goHome}
                            onNavigate={navigateFolder}
                        />
                    )}
                    {view === 'article' && (
                        <>
                            <button className="kb-breadcrumb-btn" onClick={goHome} title="Go back to Home">
                                <FaHome /> <span style={{ marginLeft: '4px' }}>Home</span>
                            </button>
                            <FaChevronRight className="kb-breadcrumb-sep kb-breadcrumb-sep-icon" style={{ marginLeft: '4px', marginRight: '4px', opacity: 0.5, fontSize: '0.8rem' }} />
                            <button className="kb-breadcrumb-btn" onClick={() => setView('folder')}>
                                ← Back to Folder
                            </button>
                        </>
                    )}
                    {view === 'editor' && (
                        <>
                            <button className="kb-breadcrumb-btn" onClick={goHome} title="Go back to Home">
                                <FaHome /> <span style={{ marginLeft: '4px' }}>Home</span>
                            </button>
                            <FaChevronRight className="kb-breadcrumb-sep kb-breadcrumb-sep-icon" style={{ marginLeft: '4px', marginRight: '4px', opacity: 0.5, fontSize: '0.8rem' }} />
                            <button className="kb-breadcrumb-btn" onClick={() => setView(currentFolderId !== 'root' ? 'folder' : 'home')}>
                                ← Cancel
                            </button>
                        </>
                    )}
                    {view === 'home' && (
                        <div className="kb-home-title">
                            <FaBook className="kb-home-title-icon" /> Knowledge Base
                        </div>
                    )}
                </div>

                {/* Search bar — hidden when on editor */}
                {view !== 'editor' && (
                    <div className="kb-search-wrap">
                        <FaSearch />
                        <input
                            className="kb-search-input"
                            placeholder="Search articles and folders…"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </div>
                )}
            </div>

            {/* Search results overlay */}
            {searchQuery.length >= 2 && view !== 'editor' && (
                <SearchResults
                    query={searchQuery}
                    onNavigateFolder={(id) => { setSearchQuery(''); navigateFolder(id); }}
                    onViewArticle={(id) => { setSearchQuery(''); viewArticle(id); }}
                />
            )}

            {/* Views */}
            {searchQuery.length < 2 && (
                <>
                    {view === 'home' && (
                        <HomeView
                            canWrite={canWrite}
                            onNavigateFolder={navigateFolder}
                            onViewArticle={viewArticle}
                            onNewArticle={openNewArticle}
                        />
                    )}

                    {view === 'folder' && (
                        <FolderBrowser
                            folderId={currentFolderId}
                            canWrite={canWrite}
                            canDelete={canDelete}
                            onNavigateFolder={navigateFolder}
                            onViewArticle={viewArticle}
                            onNewArticle={openNewArticle}
                        />
                    )}

                    {view === 'article' && (
                        <ArticleViewer
                            articleId={currentArticleId}
                            canWrite={canWrite}
                            canDelete={canDelete}
                            onEdit={() => openEditArticle(currentArticleId)}
                            onDeleted={onArticleDeleted}
                            onBack={() => setView('folder')}
                        />
                    )}

                    {view === 'editor' && (
                        <ArticleEditor
                            articleId={editingArticleId}
                            initialFolderId={currentFolderId !== 'root' ? currentFolderId : undefined}
                            onSaved={onArticleSaved}
                            onCancel={() => setView(currentFolderId !== 'root' ? 'folder' : 'home')}
                        />
                    )}
                </>
            )}
        </div>
    );
}
